import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../api/client";
import { ru } from "../locales/ru";
import { useAuth } from "./AuthContext";
import { useChat } from "./ChatContext";

const OrdersContext = createContext(null);

export function OrdersProvider({ children }) {
  const { user } = useAuth();
  const { subscribe } = useChat();
  const [orders, setOrders] = useState({});
  const [openCount, setOpenCount] = useState(0);
  const [version, setVersion] = useState(0);
  const cacheRef = useRef({ orders: {}, missing: new Set(), pending: new Set(), failed: new Set() });
  const [missingOrders, setMissingOrders] = useState(new Set());
  const [failedOrders, setFailedOrders] = useState(new Set());

  const upsert = useCallback((order) => {
    cacheRef.current.orders[order.id] = order;
    cacheRef.current.failed.delete(order.id);
    setOrders((prev) => ({ ...prev, [order.id]: order }));
  }, []);

  const refreshOpenCount = useCallback(async () => {
    try {
      const { data } = await api.get("/orders/open-count");
      setOpenCount(data.count);
    } catch {}
  }, []);

  const ensureOrders = useCallback(async (orderIds, retry = false) => {
    const cache = cacheRef.current;
    const ids = [...new Set(orderIds)].filter(id => id && !cache.orders[id] && !cache.missing.has(id) && !cache.pending.has(id) && (retry || !cache.failed.has(id)));
    ids.forEach(id => { cache.pending.add(id); cache.failed.delete(id); });
    for (let i = 0; i < ids.length; i += 50) {
      const batch = ids.slice(i, i + 50);
      try {
        const { data } = await api.post("/orders/lookup", { ids: batch });
        if (cache !== cacheRef.current) return;
        const found = new Set(data.map(o => o.id));
        data.forEach(o => { if (!cache.orders[o.id]) cache.orders[o.id] = o; });
        batch.filter(id => !found.has(id) && !cache.orders[id]).forEach(id => cache.missing.add(id));
        sessionStorage.setItem(`poehali_missing_orders_${user?.id}`, JSON.stringify([...cache.missing]));
        setOrders({ ...cache.orders });
        setMissingOrders(new Set(cache.missing));
      } catch {
        if (cache !== cacheRef.current) return;
        batch.forEach(id => cache.failed.add(id));
      } finally {
        batch.forEach(id => cache.pending.delete(id));
      }
    }
    if (cache === cacheRef.current) setFailedOrders(new Set(cache.failed));
  }, [user?.id]);

  useEffect(() => {
    let missing = [];
    if (user) {
      try { missing = JSON.parse(sessionStorage.getItem(`poehali_missing_orders_${user.id}`)) || []; } catch {}
    }
    cacheRef.current = { orders: {}, missing: new Set(missing), pending: new Set(), failed: new Set() };
    setOrders({}); setMissingOrders(new Set(missing)); setFailedOrders(new Set());
    if (!user) { setOpenCount(0); return; }
    refreshOpenCount();
  }, [user?.id, refreshOpenCount]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!subscribe || !user) return;
    return subscribe((ev) => {
      if (ev.event === "order_updated") {
        upsert(ev.data.order);
        setVersion((v) => v + 1);
        refreshOpenCount();
      } else if (ev.event === "order_notice") {
        upsert(ev.data.order);
        setVersion((v) => v + 1);
        const text = ru.orders.notice[ev.data.kind];
        if (text) toast.info(text);
      } else if (ev.event === "__reconnected") {
        setVersion((v) => v + 1);
        refreshOpenCount();
      }
    });
  }, [subscribe, user?.id, upsert, refreshOpenCount]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <OrdersContext.Provider value={{ orders, upsert, ensureOrders, missingOrders, failedOrders, openCount, version, refreshOpenCount }}>
      {children}
    </OrdersContext.Provider>
  );
}

export function useOrders() {
  return useContext(OrdersContext);
}
